import React, { useContext, memo, useRef, useCallback } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import * as S from './styles';

interface SearchBarProps {
  query: string;
  setQuery: (query: string) => void;
  clearQuery: () => void;
  placeholderText?: string;
  autoFocus?: boolean;
  autoFocusOnExpand?: boolean;
  focused?: boolean;
  className?: string;
  onFocusChange?: () => void;
  selected?: number;
  onBlurChange?: () => void;
}

const SearchBar = (props: SearchBarProps) => {
  const {
    query,
    setQuery,
    clearQuery,
    className,
    autoFocusOnExpand,
    autoFocus,
    focused,
    onFocusChange,
    selected,
    onBlurChange,
  } = props;

  const { t } = useContext(AppContext);
  const searchInputEl = useRef<HTMLInputElement>(null);

  const focusInput = (): void => {
    if (searchInputEl.current) {
      searchInputEl.current.focus();
    }
  };

  const handleClearSearchClick = useCallback(
    (event: React.MouseEvent): void => {
      event.stopPropagation();
      clearQuery();
      focusInput();
    },
    [clearQuery]
  );

  const handleSearchInputFocus = useCallback(() => {
    if (onFocusChange) {
      onFocusChange();
    }
  }, [onFocusChange]);

  const handleSearchInputBlur = useCallback(() => {
    if (onBlurChange) {
      onBlurChange();
    }
  }, [onBlurChange]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setQuery(event.target.value),
    [setQuery]
  );

  return (
    <S.SearchBar data-searchbar="true" className={className}>
      <S.InputWrapper>
        <S.SearchInput
          ref={searchInputEl}
          value={query}
          data-suggestions="search"
          autoFocus={autoFocus || autoFocusOnExpand}
          aria-expanded={focused}
          aria-owns="overlaySearchResults"
          aria-autocomplete="both"
          aria-activedescendant={selected ? `selection${selected}` : ''}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          type="search"
          placeholder={t('search', 'search.placeholder')}
          onFocus={handleSearchInputFocus}
          onBlur={handleSearchInputBlur}
          onChange={handleChange}
        />
        <S.ClearIcon
          show={query.length > 0}
          type="button"
          data-testid="clear-search-button"
          onClick={handleClearSearchClick}
        >
          <S.Close size={20} />
        </S.ClearIcon>
        <S.SearchIconContainer show={query.length === 0} type="button">
          <S.Search size={20} />
        </S.SearchIconContainer>
      </S.InputWrapper>
    </S.SearchBar>
  );
};

export default memo(SearchBar);
